/**
 * Saved measurements — rulers the user has pinned with Save so they stay drawn on
 * the chart while doing other things. Each is the ruler's points; the layer
 * redraws the line, the range circle and the per-leg labels (dimmer than the
 * active ruler). Kept on the device (localStorage) so they survive reloads.
 * Removable individually (tap on the chart) or all at once (Clear saved).
 */
import { create } from 'zustand';

export interface SavedMeasurement {
  id: string;
  points: [number, number][];
}

const LS = 'acp.savedMeasurements.v1';

function load(): SavedMeasurement[] {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS) : null;
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is SavedMeasurement =>
        !!m &&
        typeof (m as SavedMeasurement).id === 'string' &&
        Array.isArray((m as SavedMeasurement).points),
    );
  } catch {
    return [];
  }
}

function persist(items: SavedMeasurement[]): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS, JSON.stringify(items));
  } catch {
    /* ignore quota / private-mode / SSR */
  }
}

let seq = 0;

interface SavedMeasurementsState {
  items: SavedMeasurement[];
  /** Pin a ruler (needs at least two points). */
  add: (points: [number, number][]) => void;
  /** Remove one by id (tap-to-delete on the chart). */
  remove: (id: string) => void;
  /** Remove them all. */
  clear: () => void;
}

export const useSavedMeasurementsStore = create<SavedMeasurementsState>((set) => ({
  items: load(),
  add: (points) =>
    set((s) => {
      if (points.length < 2) return s;
      const id = `m${Date.now().toString(36)}-${seq++}`;
      const copy = points.map((p) => [p[0], p[1]] as [number, number]);
      const items = [...s.items, { id, points: copy }];
      persist(items);
      return { items };
    }),
  remove: (id) =>
    set((s) => {
      const items = s.items.filter((m) => m.id !== id);
      persist(items);
      return { items };
    }),
  clear: () =>
    set(() => {
      persist([]);
      return { items: [] };
    }),
}));
